import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Quote, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const testimonials = [
  {
    quote: "I went from setting goals I never finished to completing 90% of what I set out to do. The GRIT system changed everything.",
    author: "Marcus T.",
    title: "Software Engineer",
    stats: { streak: 127, grit: 48500 },
  },
  {
    quote: "The MōdCoach feels like having a drill sergeant and therapist in one. It knows when to push and when to encourage.",
    author: "Sarah K.",
    title: "Entrepreneur",
    stats: { streak: 89, grit: 32100 },
  },
  {
    quote: "Breaking my phone addiction seemed impossible. 200 days clean now. The habit tracking made it real and measurable.",
    author: "James R.",
    title: "Student",
    stats: { streak: 203, grit: 67800 },
  },
  {
    quote: "The community challenges push me to do more than I would alone. Iron truly sharpens iron here.",
    author: "Elena M.",
    title: "Fitness Coach",
    stats: { streak: 156, grit: 54200 },
  },
];

export const TestimonialCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const handlePrev = () => {
    setIsAutoPlaying(false);
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const testimonial = testimonials[activeIndex];

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-background via-card/30 to-background">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-display tracking-wider mb-4">
            FORGED BY <span className="text-gradient-ember">WARRIORS</span>
          </h2>
          <p className="text-muted-foreground">
            Real transformations from real people
          </p>
        </div>

        <div className="relative">
          {/* Main testimonial card */}
          <div className="bg-card border border-border/50 rounded-2xl p-8 md:p-12 relative overflow-hidden">
            <Quote className="absolute top-6 left-6 w-12 h-12 text-primary/20" />
            
            <div className="relative z-10">
              <blockquote className="text-xl md:text-2xl font-medium leading-relaxed mb-8 text-foreground">
                "{testimonial.quote}"
              </blockquote>

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <div className="font-display text-lg tracking-wide">
                    {testimonial.author}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {testimonial.title}
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-primary" />
                    <div>
                      <div className="font-display text-lg">{testimonial.stats.streak}</div>
                      <div className="text-xs text-muted-foreground">Day Streak</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-accent" />
                    <div>
                      <div className="font-display text-lg">{testimonial.stats.grit.toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">GRIT Points</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrev}
              className="rounded-full"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setActiveIndex(index);
                  }}
                  aria-label={`Go to testimonial ${index + 1}`}
                  className="relative flex items-center justify-center p-2"
                >
                  <span
                    className={cn(
                      "block h-2 rounded-full transition-all",
                      index === activeIndex
                        ? "w-8 bg-primary"
                        : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    )}
                  />
                </button>
              ))}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleNext}
              className="rounded-full"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
